import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Globe, TrendingUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Trigger animations after component mounts
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_paper-experts/artifacts/kt0nnq2d_F6CD1BC6-E100-4442-BB71-8CEC2533418B.PNG"
          alt="International Trade and Shipping"
          className={`w-full h-full object-cover transition-all duration-[1500ms] ease-out ${
            isLoaded ? 'scale-100 opacity-100' : 'scale-110 opacity-0'
          }`}
        />
        <div className={`absolute inset-0 bg-gradient-to-r from-slate-900/70 to-slate-800/60 transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-blue-500/30 rounded-full px-4 py-2 mb-8 transition-all duration-700 delay-300 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <Globe className="w-4 h-4 text-blue-400" />
            <span className="text-blue-100 text-sm font-medium">{t('hero.badge')}</span>
          </div>

          {/* Main Heading */}
          <h1 className={`headline-font text-5xl md:text-6xl text-white mb-6 leading-tight transition-all duration-700 delay-500 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            {t('hero.title')}
          </h1>

          {/* Subheading */}
          <p className={`text-lg md:text-xl text-gray-200 mb-12 leading-relaxed max-w-3xl mx-auto font-light transition-all duration-700 delay-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-700 delay-[900ms] ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-blue-500 text-slate-900 hover:bg-blue-400 font-semibold px-8 py-5 text-base transition-all duration-200 shadow-lg hover:shadow-xl group"
            >
              {t('hero.ctaPrimary')}
              <ArrowRight className="ms-2 w-5 h-5 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform duration-200 rtl:rotate-180" />
            </Button>
            <Button
              onClick={() => scrollToSection('sectors')}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-slate-900 font-semibold px-8 py-5 text-base transition-all duration-200"
            >
              {t('hero.ctaSecondary')}
            </Button>
          </div>

          {/* Stats */}
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 mt-20 pt-12 border-t border-blue-400/20 transition-all duration-700 delay-[1100ms] ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-white mb-2">9+</p>
              <p className="text-gray-300 text-sm">{t('hero.countriesServed')}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-white mb-2">16+</p>
              <p className="text-gray-300 text-sm">{t('hero.regionalPartners')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};

export default Hero;